import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaEdit,
  FaCheck,
  FaTimes,
  FaGift,
  FaTag, FaEye
} from 'react-icons/fa';
import { FiFilter as FilterIcon } from "react-icons/fi";
import { Drawer } from "@mui/material";
import {
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Grid,
  Box,
  IconButton,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  FormControlLabel,
  Switch,
  CircularProgress,
  Pagination,
  ToggleButton,
  Skeleton,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MdDeleteForever } from "react-icons/md";
import { postData, getData, deleteData, putData } from '../../Webservice/Webservice';

// Styled components
const VoucherCard = styled(Card)(({ theme, status, gradientIndex }) => {
  return {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    height: '170px',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: '35%',
      background: 'linear-gradient(135deg, #758092ff 0%, #53585eff 100%)',
      zIndex: 1
    }
  };
});

const CouponCodeBox = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  border: '2px dashed #e0e0e0',
  borderRadius: '8px',
  padding: '8px 8px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'monospace',
  fontWeight: 'bold',
  fontSize: '14px',
  letterSpacing: '2px',
  color: '#333',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
}));

const DiscountDisplay = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '10px 0',
}));

const GradientHeader = styled(Box)(({ theme }) => ({
  zIndex: 2,
  padding: '24px 20px',
  color: 'white',
  minHeight: '100px',
  width: '35%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: theme.spacing(4),
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  // marginTop: theme.spacing(3)
}));

const CouponsManagement = () => {
  const navigate = useNavigate();
  const { couponId } = useParams();
  const [coupons, setCoupons] = useState([]);

  // Filter state
  const [filters, setFilters] = useState({
    code: "",
    type: "",
    status: "",
    validFrom: "",
    validTill: ""
  });
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    code: "",
    type: "",
    status: "",
    validFrom: "",
    validTill: ""
  });
  const [showForm, setShowForm] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [formMode, setFormMode] = useState('edit');
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  // Validation errors state
  const [errors, setErrors] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    discount: '',
    maxDiscount: '',
    minOrderAmount: '',
    usageLimit: '',
    perUserLimit: '',
    validFrom: '',
    validTill: '',
    isFirstTimeUserOnly: false,
    applicableTo: '',
    applicableCardTypes: [],
    allowedCardBins: [],
    status: 'active',
    role_id: '',
    assignTo: ''
  });

  const fetchCoupons = async (page = 1, limit = 20) => {
    setLoading(true);
    setError(null);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const params = {
        page,
        limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""))
      };
      const res = await getData('coupon/list', params, token, false);

      if (res && res.coupons && Array.isArray(res.coupons)) {
        const transformedCoupons = res.coupons.map(coupon => ({
          id: coupon._id,
          code: coupon.code,
          type: coupon.type,
          discount: coupon.discount,
          maxDiscount: coupon.maxDiscount || null,
          minOrderAmount: coupon.minOrderAmount,
          usageLimit: coupon.usageLimit,
          perUserLimit: coupon.perUserLimit,
          validFrom: coupon.validFrom,
          validTill: coupon.validTill,
          isFirstTimeUserOnly: coupon.isFirstTimeUserOnly,
          applicableTo: coupon.applicableTo,
          applicableCardTypes: coupon.applicableCardTypes || [],
          allowedCardBins: coupon.allowedCardBins || [],
          status: coupon.status,
          used: coupon.usedBy ? coupon.usedBy.length : 0,
          assignTo: (coupon.assignTo && typeof coupon.assignTo === 'object') ? (coupon.assignTo._id || coupon.assignTo.id) : (coupon.assignTo || null),
          assignToName: (coupon.assignTo && typeof coupon.assignTo === 'object') ? (coupon.assignTo.name || `${coupon.assignTo.firstName || ''} ${coupon.assignTo.lastName || ''}`.trim() || coupon.assignTo.email) : null,
          role_id: coupon.assignTo?.role_id?._id || coupon.assignTo?.role_id?.id || null
        }));
        setCoupons(transformedCoupons);
        setMeta(res.meta || { page: 1, totalPages: 1, total: 0, limit });
      } else {
        setCoupons([]);
        setError('Failed to fetch coupons');
      }
    } catch (err) {
      setCoupons([]);
      setError('Failed to fetch coupons. Please try again.');
      console.error('Error fetching coupons:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditForm = useCallback((coupon) => {
    setIsEditing(true);
    setCurrentCoupon(coupon);
    setFormMode('view');
    setFormData({
      code: coupon.code,
      type: coupon.type || 'percentage',
      discount: coupon.discount,
      maxDiscount: coupon.maxDiscount || '',
      minOrderAmount: coupon.minOrderAmount || '',
      usageLimit: coupon.usageLimit || '',
      perUserLimit: coupon.perUserLimit || '',
      validFrom: coupon.validFrom || '',
      validTill: coupon.validTill || '',
      isFirstTimeUserOnly: coupon.isFirstTimeUserOnly || false,
      applicableTo: coupon.applicableTo || '',
      applicableCardTypes: coupon.applicableCardTypes || [''],
      allowedCardBins: coupon.allowedCardBins || [''],
      status: coupon.status || 'active',
      role_id: coupon.role_id || '',
      assignTo: (coupon.assignTo && typeof coupon.assignTo === 'object') ? (coupon.assignTo._id || coupon.assignTo.id) : (coupon.assignTo || '')
    });
    setErrors({});
    setShowForm(true);
    // push URL with coupon id if not already present
    if (!couponId || couponId !== (coupon.id || coupon._id)) {
      navigate(`/couponsmanagement/${coupon.id || coupon._id}`);
    }
  }, [couponId, navigate]);

  useEffect(() => {
    fetchCoupons(meta.page, meta.limit);
  }, [meta.page, meta.limit, filters]);

  // When a couponId exists in the URL, open the form in view mode once coupons are loaded
  useEffect(() => {
    if (!couponId) return;
    if (loading) return;
    if (!coupons || coupons.length === 0) return;
    const match = coupons.find(c => c.id === couponId || c._id === couponId);
    if (match) {
      handleOpenEditForm(match);
    }
  }, [couponId, coupons, loading, handleOpenEditForm]);

  // Fetch roles and users when the form is opened
  useEffect(() => {
    if (!showForm) return;
    const tokenValue = sessionStorage.getItem('token');
    const token = tokenValue ? `Bearer ${tokenValue}` : '';
    getData('user/role', undefined, token, false).then((res) => {
      if (res && Array.isArray(res)) {
        setRoles(res);
      } else if (res && Array.isArray(res.roles)) {
        setRoles(res.roles);
      }
    });
    getData('user/list', undefined, token, false).then((res) => {
      if (res && Array.isArray(res)) {
        setUsers(res);
      } else if (res && Array.isArray(res.users)) {
        setUsers(res.users);
      }
    });
  }, [showForm]);




  const handleOpenAddForm = () => {
    setIsEditing(false);
    setCurrentCoupon(null);
    setFormMode('edit');
    setFormData({
      code: '',
      type: 'percentage',
      discount: '',
      maxDiscount: '',
      minOrderAmount: '',
      usageLimit: '',
      perUserLimit: '',
      validFrom: '',
      validTill: '',
      isFirstTimeUserOnly: false,
      applicableTo: '',
      applicableCardTypes: [],
      allowedCardBins: [],
      status: 'active',
      role_id: '',
      assignTo: ''
    });
    setErrors({});
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    // navigate back to list route
    navigate('/couponsmanagement');
  };

  const handleFormModeChange = () => {
    setFormMode(prevMode => prevMode === 'view' ? 'edit' : 'view');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'role_id' ? { assignTo: '' } : {})
    }));
  };

  const filteredUsers = useMemo(() => {
    if (!formData.role_id) return users;
    return users.filter((u) => {
      const userRoleId = typeof u.role_id === 'object' && u.role_id !== null ? (u.role_id._id || u.role_id.id) : u.role_id;
      return userRoleId === formData.role_id;
    });
  }, [users, formData.role_id]);

  // ---------- Validation function ----------
  const validateForm = () => {
    const newErrors = {};

    // code
    if (!formData.code || !formData.code.toString().trim()) {
      newErrors.code = 'Coupon code is required';
    }

    // discount
    const discountVal = formData.discount === '' || formData.discount === null ? '' : Number(formData.discount);
    if (formData.type === 'percentage') {
      if (formData.discount === '' || formData.discount === null || isNaN(discountVal)) {
        newErrors.discount = 'Discount percentage is required';
      } else if (discountVal <= 0 || discountVal > 100) {
        newErrors.discount = 'Enter percentage between 1 and 100';
      }
      // maxDiscount optional but if provided must be >= 0
      if (formData.maxDiscount !== '' && formData.maxDiscount !== null) {
        const md = Number(formData.maxDiscount);
        if (isNaN(md) || md < 0) newErrors.maxDiscount = 'Max discount must be a non-negative number';
      }
    } else if (formData.type === 'fixed' || formData.type === 'flat') {
      if (formData.discount === '' || formData.discount === null || isNaN(discountVal)) {
        newErrors.discount = 'Discount amount is required';
      } else if (discountVal <= 0) {
        newErrors.discount = 'Discount must be greater than 0';
      }
    } else {
      // fallback for other types
      if (formData.discount !== '' && formData.discount !== null && isNaN(discountVal)) {
        newErrors.discount = 'Discount must be numeric';
      }
    }

    // minOrderAmount
    const minOrder = Number(formData.minOrderAmount);
    if (formData.minOrderAmount === '' || formData.minOrderAmount === null || isNaN(minOrder)) {
      newErrors.minOrderAmount = 'Minimum order amount is required';
    } else if (minOrder < 0) {
      newErrors.minOrderAmount = 'Minimum order amount cannot be negative';
    }

    // usageLimit, perUserLimit (optional)
    const usageLimit = Number(formData.usageLimit);
    const perUserLimit = Number(formData.perUserLimit);
    if (formData.usageLimit !== '' && formData.usageLimit !== null) {
      if (isNaN(usageLimit) || !Number.isInteger(usageLimit) || usageLimit <= 0) {
        newErrors.usageLimit = 'Usage limit must be a positive integer';
      }
    }
    if (formData.perUserLimit !== '' && formData.perUserLimit !== null) {
      if (isNaN(perUserLimit) || !Number.isInteger(perUserLimit) || perUserLimit <= 0) {
        newErrors.perUserLimit = 'Per user limit must be a positive integer';
      } else if (formData.usageLimit !== '' && formData.usageLimit !== null && !isNaN(usageLimit) && perUserLimit > usageLimit) {
        newErrors.perUserLimit = 'Per user limit cannot exceed overall usage limit';
      }
    }

    // validFrom / validTill
    if (!formData.validFrom) {
      newErrors.validFrom = 'Valid from date is required';
    }
    if (!formData.validTill) {
      newErrors.validTill = 'Valid till date is required';
    }
    if (formData.validFrom && formData.validTill) {
      const from = new Date(formData.validFrom).getTime();
      const till = new Date(formData.validTill).getTime();
      if (isNaN(from) || isNaN(till)) {
        if (isNaN(from)) newErrors.validFrom = 'Invalid start date';
        if (isNaN(till)) newErrors.validTill = 'Invalid end date';
      } else if (from >= till) {
        newErrors.validTill = 'Valid Till must be after Valid From';
      }
    }

    // applicableTo
    if (!formData.applicableTo || !formData.applicableTo.toString().trim()) {
      newErrors.applicableTo = 'Please select applicable audience';
    }

    // applicableCardTypes (optional)
    const cardTypes = Array.isArray(formData.applicableCardTypes) ? formData.applicableCardTypes.filter(Boolean) : (typeof formData.applicableCardTypes === 'string' ? formData.applicableCardTypes.split(',').map(t => t.trim()).filter(Boolean) : []);

    // allowedCardBins: validate each bin must be 4-6 digits if provided
    const bins = Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins : (typeof formData.allowedCardBins === 'string' ? formData.allowedCardBins.split(',').map(b => b.trim()).filter(Boolean) : []);
    if (bins.length > 0) {
      const invalidBins = bins.filter(b => !/^\d{4,6}$/.test(b));
      if (invalidBins.length > 0) {
        newErrors.allowedCardBins = `Invalid BIN(s): ${invalidBins.join(', ')} — use 4 to 6 digits`;
      }
    }

    // status
    if (!['active', 'inactive', 'expired'].includes(formData.status)) {
      newErrors.status = 'Invalid status selected';
    }

    // if role_id set and assignTo set, ensure assignTo matches role (we'll not enforce strictly, just optionally warn)
    if (formData.role_id && formData.assignTo) {
      const found = (users || []).find(u => (u._id || u.id) === formData.assignTo);
      if (found) {
        const userRoleId = typeof found.role_id === 'object' && found.role_id !== null ? (found.role_id._id || found.role_id.id) : found.role_id;
        if (userRoleId !== formData.role_id) {
          newErrors.assignTo = 'Selected user does not belong to selected role';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Clear individual error as user types
  useEffect(() => {
    setErrors(prev => {
      const next = { ...prev };

      if (next.code && formData.code && formData.code.toString().trim()) delete next.code;
      if (next.discount && formData.discount !== '' && !isNaN(Number(formData.discount))) delete next.discount;
      if (next.maxDiscount && formData.maxDiscount !== '' && !isNaN(Number(formData.maxDiscount))) delete next.maxDiscount;
      if (next.minOrderAmount && formData.minOrderAmount !== '' && !isNaN(Number(formData.minOrderAmount))) delete next.minOrderAmount;
      if (next.usageLimit && formData.usageLimit !== '' && Number.isInteger(Number(formData.usageLimit)) && Number(formData.usageLimit) > 0) delete next.usageLimit;
      if (next.perUserLimit && formData.perUserLimit !== '' && Number.isInteger(Number(formData.perUserLimit)) && Number(formData.perUserLimit) > 0) delete next.perUserLimit;
      if ((next.validFrom || next.validTill) && formData.validFrom && formData.validTill) {
        const from = new Date(formData.validFrom).getTime();
        const till = new Date(formData.validTill).getTime();
        if (!isNaN(from) && !isNaN(till) && from < till) {
          delete next.validFrom;
          delete next.validTill;
        }
      }
      if (next.applicableTo && formData.applicableTo) delete next.applicableTo;
      if (next.applicableCardTypes && formData.applicableCardTypes && (Array.isArray(formData.applicableCardTypes) ? formData.applicableCardTypes.length > 0 : formData.applicableCardTypes)) delete next.applicableCardTypes;
      if (next.allowedCardBins) {
        const bins = Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins : (typeof formData.allowedCardBins === 'string' ? formData.allowedCardBins.split(',').map(b => b.trim()).filter(Boolean) : []);
        const invalidBins = bins.filter(b => !/^\d{4,6}$/.test(b));
        if (bins.length > 0 && invalidBins.length === 0) delete next.allowedCardBins;
      }
      if (next.status && ['active', 'inactive', 'expired'].includes(formData.status)) delete next.status;
      if (next.assignTo && formData.assignTo) delete next.assignTo;

      return next;
    });
  }, [formData, users]);

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    // Validate
    if (!validateForm()) return;

    if (isSubmitting) return;

    setIsSubmitting(true);
    console.log('Submitting form data:', formData);

    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';

      if (isEditing) {
        const payload = {
          ...formData,
          discount: Number(formData.discount),
          maxDiscount: (formData.maxDiscount !== '' && formData.maxDiscount !== null) ? Number(formData.maxDiscount) : null,
          minOrderAmount: Number(formData.minOrderAmount),
          usageLimit: formData.usageLimit ? Number(formData.usageLimit) : null,
          perUserLimit: formData.perUserLimit ? Number(formData.perUserLimit) : null,
          allowedCardBins: (Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins : (typeof formData.allowedCardBins === 'string' ? formData.allowedCardBins.split(',').map(b => b.trim()).filter(Boolean) : [])).filter(bin => bin.trim() !== ''),
          role_id: formData.role_id || null,
          assignTo: formData.assignTo || null,
        };

        const response = await putData(
          `coupon/update/${currentCoupon.id}`,
          payload,
          token
        );
        
        if (response) {
          await fetchCoupons(meta.page, meta.limit);
          handleCloseForm();
        }
      } else {
        const couponData = {
          code: formData.code,
          type: formData.type,
          discount: Number(formData.discount),
          maxDiscount: (formData.maxDiscount !== '' && formData.maxDiscount !== null) ? Number(formData.maxDiscount) : null,
          minOrderAmount: Number(formData.minOrderAmount),
          usageLimit: formData.usageLimit ? Number(formData.usageLimit) : null,
          perUserLimit: formData.perUserLimit ? Number(formData.perUserLimit) : null,
          validFrom: formData.validFrom,
          validTill: formData.validTill,
          isFirstTimeUserOnly: formData.isFirstTimeUserOnly,
          applicableTo: formData.applicableTo,
          applicableCardTypes: formData.applicableCardTypes,
          allowedCardBins: (Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins : (typeof formData.allowedCardBins === 'string' ? formData.allowedCardBins.split(',').map(b => b.trim()).filter(Boolean) : [])).filter(bin => bin.trim() !== ''),
          status: formData.status,
          role_id: formData.role_id || null,
          assignTo: formData.assignTo || null
        };

        const response = await postData('coupon/add', couponData, token);

        if (response) {
          await fetchCoupons(meta.page, meta.limit);
          handleCloseForm();
        }
      }
    } catch (error) {
      console.error('Error submitting coupon:', error);
      setError('Failed to submit coupon. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const response = await deleteData(`coupon/delete/${id}`, token);
      if (response) {
        setCoupons(coupons.filter(coupon => coupon.id !== id));
        await fetchCoupons(meta.page, meta.limit);
      } else {
        setError('Failed to delete coupon');
      }
    } catch (error) {
      setError('Failed to delete coupon. Please try again.');
      console.error('Error deleting coupon:', error);
    }
  };



  const getStatusConfig = (status) => {
    switch (status) {
      case 'active':
        return {
          icon: <FaCheck />,
          color: 'success',
          text: 'Active'
        };
      case 'expired':
        return {
          icon: <FaTimes />,
          color: 'error',
          text: 'Inactive'
        };
      default:
        return {
          icon: <FaTimes />,
          color: 'default',
          text: 'Unknown'
        };
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.type === 'percentage') return `${coupon.discount}%`;
    if (coupon.type === 'flat' || coupon.type === 'fixed') return `₹${coupon.discount}`;
  };

  const handlePageChange = (event, newPage) => {
    setMeta(prev => ({ ...prev, page: newPage }));
  };

  const formatToDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const renderViewField = (label, value) => (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography>{value || '-'}</Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 3 }}>
      <Box sx={{ maxWidth: '1600px', mx: 'auto' }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {showForm ? (
          <Box>
            {/* Coupon Form */}
            <FormContainer>
              {/* Form Header with Back Button */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', fontFamily: 'Mulish, sans-serif' }}>
                  {isEditing ? (formMode === 'edit' ? 'Edit Coupon' : 'View Coupon') : 'Add New Coupon'}
                </Typography>

                <Box display="flex" alignItems="center" gap={2}>
                  {isEditing && (
                    <ToggleButton
                      value={formMode}
                      onClick={handleFormModeChange}
                      aria-label={formMode === 'view' ? 'switch to edit mode' : 'switch to view mode'}
                      size="small"
                      sx={{
                        fontFamily: 'Mulish, sans-serif',
                        color: 'black',
                        background: 'rgba(0, 0, 0, 0.08)',
                        '&:hover': { background: 'rgba(0, 0, 0, 0.08)' }
                      }}
                    >
                      {formMode === 'view' ? (
                        <>
                          <FaEdit style={{ marginRight: 8 }} />
                          Edit
                        </>
                      ) : (
                        <>
                          <FaEye style={{ marginRight: 8 }} />
                          View
                        </>
                      )}
                    </ToggleButton>
                  )}
                  <Button
                    variant="contained"
                    // startIcon={<FaArrowLeft />}
                    onClick={handleCloseForm}
                    sx={{
                      color: 'white',
                      backgroundColor: 'red',
                      fontFamily: 'Mulish, sans-serif',
                      '&:hover': {
                        bgcolor: 'red',
                      },
                    }}
                  >
                    Back
                  </Button>
                </Box>
              </Box>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    {formMode === 'view' ? (
                      renderViewField('Coupon Code', formData.code)
                    ) : (
                      <TextField
                        fullWidth
                        label="Coupon Code"
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                        error={!!errors.code}
                        helperText={errors.code}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {formMode === 'view' ? (
                      renderViewField('Discount Type', formData.type === 'percentage' ? 'Percentage' : formData.type === 'fixed' ? 'Flat' : formData.type)
                    ) : (
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Discount Type</InputLabel>
                        <Select
                          name="type"
                          value={formData.type}
                          onChange={(e) => {
                            // reset certain fields when type changes
                            setFormData(prev => ({ ...prev, type: e.target.value, discount: '', maxDiscount: '' }));
                          }}
                          label="Discount Type"
                          required
                        >
                          <MenuItem value="percentage">Percentage</MenuItem>
                          <MenuItem value="fixed">Flat</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {formMode === 'view' ? (
                      renderViewField(
                        formData.type === 'percentage' ? 'Discount Percentage' : 'Discount Amount',
                        formData.type === 'percentage' ? `${formData.discount}%` : formData.type === 'fixed' ? `₹${formData.discount}` : formData.discount
                      )
                    ) : (
                      <TextField
                        fullWidth
                        label={formData.type === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
                        name="discount"
                        value={formData.discount}
                        onChange={handleInputChange}
                        required={formData.type !== 'free_shipping'}
                        disabled={formData.type === 'free_shipping'}
                        type={formData.type === 'free_shipping' ? 'text' : 'number'}
                        variant="outlined"
                        InputProps={{
                          endAdornment: formData.type === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : formData.type === 'fixed' ? <InputAdornment position="end">₹</InputAdornment> : null,
                        }}
                        error={!!errors.discount}
                        helperText={errors.discount}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {formMode === 'view' ? (
                      renderViewField('Minimum Order Amount', formData.minOrderAmount ? `₹${formData.minOrderAmount}` : '-')
                    ) : (
                      <TextField
                        fullWidth
                        label="Minimum Order Amount (₹)"
                        name="minOrderAmount"
                        value={formData.minOrderAmount}
                        onChange={handleInputChange}
                        type="number"
                        required
                        variant="outlined"
                        error={!!errors.minOrderAmount}
                        helperText={errors.minOrderAmount}
                      />
                    )}
                  </Grid>

                  {formData.type === 'percentage' && (
                    <Grid item xs={12} sm={6}>
                      {formMode === 'view' ? (
                        renderViewField('Maximum Discount', formData.maxDiscount ? `₹${formData.maxDiscount}` : 'Not set')
                      ) : (
                        <TextField
                          fullWidth
                          label="Maximum Discount (₹)"
                          name="maxDiscount"
                          value={formData.maxDiscount}
                          onChange={handleInputChange}
                          type="number"
                          variant="outlined"
                          error={!!errors.maxDiscount}
                          helperText={errors.maxDiscount || 'Optional - cap the discount amount'}
                        />
                      )}
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    {formMode === 'view' ? (
                      renderViewField('Usage Limit', formData.usageLimit)
                    ) : (
                      <TextField
                        fullWidth
                        label="Usage Limit"
                        name="usageLimit"
                        value={formData.usageLimit}
                        onChange={handleInputChange}
                        type="number"
                        variant="outlined"
                        error={!!errors.usageLimit}
                        helperText={errors.usageLimit}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {formMode === 'view' ? (
                      renderViewField('Per User Limit', formData.perUserLimit)
                    ) : (
                      <TextField
                        fullWidth
                        label="Per User Limit"
                        name="perUserLimit"
                        value={formData.perUserLimit}
                        onChange={handleInputChange}
                        type="number"
                        variant="outlined"
                        error={!!errors.perUserLimit}
                        helperText={errors.perUserLimit}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {formMode === 'view' ? (
                      renderViewField('Valid From', formData.validFrom ? formatToDDMMYYYY(formData.validFrom) : 'Not set')
                    ) : (
                      <Box>
                        <TextField
                          fullWidth
                          label="Valid From"
                          name="validFrom"
                          type="datetime-local"
                          value={formData.validFrom}
                          onChange={handleInputChange}
                          InputLabelProps={{ shrink: true }}
                          required
                          variant="outlined"
                          error={!!errors.validFrom}
                          helperText={errors.validFrom}
                        />
                        {formData.validFrom && !errors.validFrom && (
                          <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                            Format: {formatToDDMMYYYY(formData.validFrom)}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {formMode === 'view' ? (
                      renderViewField('Valid Till', formData.validTill ? formatToDDMMYYYY(formData.validTill) : 'Not set')
                    ) : (
                      <Box>
                        <TextField
                          fullWidth
                          label="Valid Till"
                          name="validTill"
                          type="datetime-local"
                          value={formData.validTill}
                          onChange={handleInputChange}
                          InputLabelProps={{ shrink: true }}
                          required
                          variant="outlined"
                          error={!!errors.validTill}
                          helperText={errors.validTill}
                        />
                        {formData.validTill && !errors.validTill && (
                          <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                            Format: {formatToDDMMYYYY(formData.validTill)}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    {formMode === 'view' ? (
                      renderViewField('Status', formData.status.charAt(0).toUpperCase() + formData.status.slice(1))
                    ) : (
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Status</InputLabel>
                        <Select
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                          label="Status"
                          required
                        >
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="inactive">Inactive</MenuItem>
                          <MenuItem value="expired">Expired</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    {formMode === 'view' ? (
                      renderViewField('First Time Users Only', formData.isFirstTimeUserOnly ? 'Yes' : 'No')
                    ) : (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isFirstTimeUserOnly}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                isFirstTimeUserOnly: e.target.checked,
                              }))
                            }
                            name="isFirstTimeUserOnly"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: 'rgb(255, 209, 2)',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: 'rgba(245, 216, 87, 1)',
                              },
                            }}
                          />
                        }
                        label="First Time Users Only"
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {formMode === 'view' ? (
                      renderViewField('Role', (roles.find(r => r._id === formData.role_id)?.name) || '-')
                    ) : (
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Role</InputLabel>
                        <Select
                          name="role_id"
                          value={formData.role_id}
                          onChange={handleInputChange}
                          label="Role"
                        >
                          {roles && roles.length > 0 ? (
                            roles.map((role) => (
                              <MenuItem key={role._id || role.id} value={role._id || role.id}>
                                {(role.name || '').charAt(0).toUpperCase() + (role.name || '').slice(1)}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>Loading roles...</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {formMode === 'view' ? (
                      renderViewField('Assign To', (() => {
                        if (!formData.assignTo) return '-';
                        const assignedUser = users.find(u => (u._id || u.id) === formData.assignTo);
                        if (assignedUser) {
                          return assignedUser.name || `${assignedUser.firstName || ''} ${assignedUser.lastName || ''}`.trim() || assignedUser.email;
                        }
                        // Fallback if users list not loaded or user not found, 
                        // but we have assignToName from transformed coupons?
                        // Actually let's just use currentCoupon.assignToName if available
                        return currentCoupon?.assignToName || formData.assignTo || '-';
                      })())
                    ) : (
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Assign To</InputLabel>
                        <Select
                          name="assignTo"
                          value={formData.assignTo}
                          onChange={handleInputChange}
                          label="Assign To"
                          disabled={!formData.role_id}
                          error={!!errors.assignTo}
                        >
                          {!formData.role_id ? (
                            <MenuItem disabled>Select role first</MenuItem>
                          ) : filteredUsers && filteredUsers.length > 0 ? (
                            filteredUsers.map((user) => (
                              <MenuItem key={user._id || user.id} value={user._id || user.id}>
                                {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>No users for selected role</MenuItem>
                          )}
                        </Select>
                        {errors.assignTo && <Typography variant="caption" color="error">{errors.assignTo}</Typography>}
                      </FormControl>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    {formMode === 'view' ? (
                      renderViewField('Applicable To', formData.applicableTo || '-')
                    ) : (
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Applicable To*</InputLabel>
                        <Select
                          name="applicableTo"
                          value={formData.applicableTo || ''}
                          onChange={(e) =>
                            setFormData(prev => ({
                              ...prev,
                              applicableTo: e.target.value
                            }))
                          }
                          label="Applicable To"
                          required
                          error={!!errors.applicableTo}
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="b2b">B2B</MenuItem>
                          <MenuItem value="b2c">B2C</MenuItem>
                        </Select>
                        {errors.applicableTo && <Typography variant="caption" color="error">{errors.applicableTo}</Typography>}
                      </FormControl>
                    )}
                  </Grid>

                  <Grid item sm={12}>
                    {formMode === 'view' ? (
                      renderViewField('Applicable Card Types', formData.applicableCardTypes)
                    ) : (
                      <TextField
                        fullWidth
                        label="Applicable Card Types"
                        name="applicableCardTypes"
                        value={Array.isArray(formData.applicableCardTypes) ? formData.applicableCardTypes.join(', ') : formData.applicableCardTypes}
                        onChange={(e) => {
                          const val = e.target.value;
                          const arr = val.split(',').map(v => v.trim()).filter(Boolean);
                          setFormData(prev => ({ ...prev, applicableCardTypes: arr }));
                        }}
                        variant="outlined"
                        error={!!errors.applicableCardTypes}
                        helperText={errors.applicableCardTypes}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    {formMode === 'view' ? (
                      renderViewField('Allowed Card Bins', Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins.join(', ') : formData.allowedCardBins)
                    ) : (
                      <TextField
                        fullWidth
                        label="Allowed Card Bins"
                        name="allowedCardBins"
                        value={Array.isArray(formData.allowedCardBins) ? formData.allowedCardBins.join(', ') : formData.allowedCardBins}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          allowedCardBins: e.target.value.split(',').map(bin => bin.trim()).filter(bin => bin)
                        }))}
                        variant="outlined"
                        placeholder="431581, 123456"
                        helperText={errors.allowedCardBins ? errors.allowedCardBins : "Enter first 4-6 digits of card numbers, separated by commas"}
                        error={!!errors.allowedCardBins}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <Button
                        onClick={handleCloseForm}
                        variant="outlined"
                        sx={{
                          textTransform: 'none',
                          minWidth: '120px',
                          color: 'red',
                          border: '1px solid red',
                          '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.1)', border: '1px solid red', },
                        }}
                      >
                        Cancel
                      </Button>
                      {formMode === 'edit' && (
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={isSubmitting}
                          sx={{
                            bgcolor: 'rgb(255 209 2)',
                            color: 'black',
                            border: '1px solid black',
                            '&:hover': { bgcolor: 'rgb(255 209 2)' },
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: '120px'
                          }}
                        >
                          {isSubmitting ? (
                            <>
                              <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                              {isEditing ? 'Updating...' : 'Creating...'}
                            </>
                          ) : (
                            isEditing ? 'Update' : 'Create'
                          )}
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </FormContainer>
          </Box>
        ) : (
          <Box>
            {/* Header & Controls */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
              flexWrap: 'wrap',
              gap: 2
            }}>
              <Typography variant="h3"
                component="h1"
                sx={{
                  color: "#1a202c",
                  fontFamily: "Mulish, sans-serif"
                }}>
                Coupons Management
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setTempFilters(filters);
                    setFilterDrawerOpen(true);
                  }}
                  sx={{
                    fontFamily: 'Mulish, sans-serif',
                    borderRadius: '10px',
                    color: '#4b5563',
                    borderColor: '#e5e7eb',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    height: '40px',
                    px: 2,
                    '&:hover': {
                      borderColor: '#d1d5db',
                      backgroundColor: '#f9fafb'
                    }
                  }}
                >
                  Filters
                </Button>

                <Button
                  variant="contained"
                  startIcon={<FaTag />}
                  onClick={handleOpenAddForm}
                  sx={{
                    fontFamily: 'Mulish, sans-serif',
                    background: 'rgb(255 209 2)',
                    color: 'black',
                    textTransform: 'none',
                    border: '1px solid black',
                    borderRadius: 2,
                    boxShadow: 'none',
                    height: '40px',
                    '&:hover': {
                      boxShadow: 'none',
                      background: 'rgb(255 209 2)',
                    }
                  }}
                >
                  Add Coupon
                </Button>
              </Box>
            </Box>


            {/* Filter Drawer UI */}
            <Drawer
              anchor="right"
              open={filterDrawerOpen}
              onClose={() => setFilterDrawerOpen(false)}
            >
              <Box sx={{ width: 350, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>
                  Filters
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Coupon Code"
                  size="small"
                  value={tempFilters.code}
                  onChange={e => setTempFilters({ ...tempFilters, code: e.target.value })}
                  sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
                />

                <TextField
                  select
                  fullWidth
                  label="Discount Type"
                  variant="outlined"
                  size="small"
                  value={tempFilters.type}
                  onChange={e => setTempFilters({ ...tempFilters, type: e.target.value })}
                  sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
                >
                  <MenuItem value="">Any Type</MenuItem>
                  <MenuItem value="flat">Flat</MenuItem>
                  <MenuItem value="percentage">Percentage</MenuItem>
                </TextField>

                <TextField
                  select
                  fullWidth
                  label="Status"
                  variant="outlined"
                  size="small"
                  value={tempFilters.status}
                  onChange={e => setTempFilters({ ...tempFilters, status: e.target.value })}
                  sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
                >
                  <MenuItem value="">Any Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Valid From"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={tempFilters.validFrom}
                  onChange={e => setTempFilters({ ...tempFilters, validFrom: e.target.value })}
                  sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Valid Till"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={tempFilters.validTill}
                  onChange={e => setTempFilters({ ...tempFilters, validTill: e.target.value })}
                  sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
                />

                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      const cleared = { code: "", type: "", status: "", validFrom: "", validTill: "" };
                      setTempFilters(cleared);
                      setFilters(cleared);
                      setMeta(prev => ({ ...prev, page: 1 }));
                      setFilterDrawerOpen(false);
                    }}
                    sx={{
                      fontFamily: 'Mulish, sans-serif',
                      borderRadius: '10px',
                      color: '#4b5563',
                      borderColor: '#e5e7eb',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { borderColor: '#d1d5db', backgroundColor: '#f9fafb' }
                    }}
                  >
                    Clear All
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setFilters(tempFilters);
                      setMeta(prev => ({ ...prev, page: 1 }));
                      setFilterDrawerOpen(false);
                    }}
                    sx={{
                      fontFamily: 'Mulish, sans-serif',
                      borderRadius: '10px',
                      background: 'rgb(255 209 2)',
                      color: 'black',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
                      border: '1px solid black',
                      '&:hover': {
                        background: 'rgb(255 209 2)',
                        boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Drawer>

            {/* Coupons Grid */}
            {loading ? (
              <Grid container spacing={2}>
                {Array.from({ length: 8 }).map((_, idx) => (
                  <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={idx}>
                    <Card sx={{ borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '170px' }}>
                      <Box sx={{ display: 'flex', height: '100%' }}>
                        <Box sx={{ p: 3, width: '35%', background: 'linear-gradient(90deg, #f3f4f6, #e5e7eb)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                          <Skeleton variant="rectangular" width={80} height={60} />
                          <Box mt={2}>
                            <Skeleton variant="rounded" width={60} height={20} />
                          </Box>
                        </Box>
                        <Box sx={{ p: 2, width: '65%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1 }}>
                            <Box sx={{ p: 1, width: '50%' }}>
                              <Skeleton variant="text" width="80%" />
                              <Skeleton variant="rounded" width="100%" height={32} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width="60%" />
                              <Skeleton variant="text" width="40%" />
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                            <Skeleton variant="circular" width={32} height={32} />
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : coupons.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <Typography variant="h6" sx={{ color: '#64748b' }}>
                  No coupons found
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {coupons.map((coupon) => {
                  const statusConfig = getStatusConfig(coupon.status);
                  // Note: usagePercentage could be used for progress indicators if needed

                  return (
                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={coupon.id}>
                      <VoucherCard>
                        {/* Gradient Header */}
                        <GradientHeader onClick={() => handleOpenEditForm(coupon)}>
                          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 1 }}>
                            <img src="/img/gift.png" alt="gift" style={{ width: '80%', maxWidth: '80px', height: 'auto' }} />
                          </Box>
                          <Chip
                            label={statusConfig.text}
                            color={statusConfig.color}
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />

                          <FaGift style={{
                            position: 'absolute',
                            bottom: '10px',
                            right: '10px',
                            opacity: 0.1,
                            fontSize: '24px'
                          }} />
                        </GradientHeader>

                        <CardContent sx={{
                          p: '12px !important',
                          width: '65%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 1,
                              cursor: 'pointer'
                            }}
                            onClick={() => handleOpenEditForm(coupon)}
                          >
                            {/* Discount Display */}
                            <DiscountDisplay sx={{
                              width: '100%',
                              borderBottom: '1px dashed #e2e8f0',
                              pb: 1
                            }}>
                              <Typography variant="h5" sx={{ fontWeight: '800', color: '#1e293b', lineHeight: 1 }}>
                                {formatDiscount(coupon)}
                              </Typography>
                              <Typography variant="caption" sx={{
                                color: '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: 700,
                                fontSize: '0.7rem'
                              }}>
                                {coupon.type === 'free_shipping' ? 'Shipping' : 'Discount'}
                              </Typography>
                            </DiscountDisplay>

                            {/* Coupon Code */}
                            <Box sx={{ textAlign: 'center', width: '100%' }}>
                              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '10px', display: 'block', mb: 0.5 }}>
                                COUPON CODE
                              </Typography>
                              <CouponCodeBox sx={{
                                transform: 'scale(0.9)',
                                padding: '4px 8px',
                                letterSpacing: '1px',
                                fontSize: '12px'
                              }}>
                                {coupon.code}
                              </CouponCodeBox>
                            </Box>
                          </Box>

                          {/* Actions & AssignTo */}
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            px: 2,
                            pt: 1,
                          }}>
                            {/* <Box>
                              {coupon.assignToName && (
                                <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                                  To: {coupon.assignToName}
                                </Typography>
                              )}
                            </Box> */}
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(coupon.id)}
                              sx={{
                                '&:hover': { bgcolor: '#fef2f2' }
                              }}
                            >
                              <MdDeleteForever style={{ color: 'red', fontSize: '1.4rem' }} />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </VoucherCard>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* Pagination */}
            {meta.total > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={meta.totalPages}
                  page={meta.page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  disabled={loading}
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPagination-ul': { flexWrap: 'nowrap' },
                    '& .MuiPaginationItem-root': {
                      fontFamily: 'Mulish, sans-serif',
                      fontWeight: 600,
                      borderRadius: '8px',
                      '&.Mui-selected': {
                        backgroundColor: 'rgb(255, 209, 2)',
                        color: 'black',
                        '&:hover': {
                          backgroundColor: 'rgb(255, 190, 0)',
                        }
                      }
                    }
                  }}
                />
              </Box>
            )}
            {meta.total > 0 && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                Showing {(meta.page - 1) * meta.limit + 1} to {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} entries
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CouponsManagement;
